/**
 * Where a record stands in its lifecycle: which version is in use. Not one of the three status
 * families (validation, verification, workflow). Type Curves and DCA declines derive it from their
 * fields; every other Promoted, Draft, Archived or Default tag in the app renders it too, so a word
 * always has one colour.
 */
export type LifecycleStatus = 'promoted' | 'draft' | 'archived' | 'default';

/**
 * Which record the tag describes; picks the class and the hover wording. Omitted for the other
 * records that use the same words (well workspaces, structure grids, landing interpretations...).
 */
export type LifecycleSubject = 'type-curve' | 'dca';

/** The Carbon tag types the lifecycle statuses use. */
export type LifecycleTagType = 'green' | 'red' | 'blue';

export interface LifecycleConfig {
  label: string;
  /** Carbon tag type. Undefined for Draft, which is yellow, and Carbon has no yellow tag. */
  type?: LifecycleTagType;
  /** Paint with the yellow class that mirrors Carbon's tag recipe on the yellow palette. */
  yellow?: boolean;
  /** Hover text per subject. A status a subject never takes has no entry. */
  description: Partial<Record<LifecycleSubject, string>>;
}

export const LIFECYCLE_STATUS_CONFIG: Record<LifecycleStatus, LifecycleConfig> =
  {
    promoted: {
      label: 'Promoted',
      type: 'green',
      description: {
        'type-curve': 'Promoted: published from its Type Curve workspace',
        'dca': 'Promoted: the decline in use for this well and DCA scenario',
      },
    },
    draft: {
      label: 'Draft',
      yellow: true,
      description: {
        'type-curve': 'Draft: saved in a workspace and not promoted',
        'dca': 'Draft: saved in a DCA workspace and not promoted',
      },
    },
    archived: {
      label: 'Archived',
      type: 'red',
      description: {
        'type-curve':
          'Archived: retired, and no longer offered to new evaluations',
        'dca':
          'Archived: promoted once, since replaced by a newer promoted decline',
      },
    },
    default: {
      label: 'Default',
      type: 'blue',
      description: {
        'type-curve':
          'Default: the promoted Type Curve evaluations pick for this area and formation',
      },
    },
  };

/** The lifecycle fields a Type Curve version carries, under any of the names the APIs use. */
export interface TypeCurveLifecycleFields {
  /** Retired: the version itself, or its Type Curve Area, was archived. */
  archived?: boolean | null;
  /** The zone default evaluations pre-select (TypeCurve::HeaderDetail#is_default). */
  isDefault?: boolean | null;
  /** Promoted from its workspace, or the family's current promoted version. */
  promoted?: boolean | null;
}

/**
 * One status per Type Curve version, most specific first: Archived, then Default, then
 * Promoted, then Draft. Default implies Promoted (TypeCurve::DefaultVersion only elects a current
 * promoted version), so a default curve shows Default alone. Returns null when the record says
 * nothing about promotion, so a list that never loaded the flag shows no tag rather than a wrong
 * Draft.
 *
 * @param {TypeCurveLifecycleFields} fields - The version's lifecycle fields.
 * @return {LifecycleStatus | null} The status, or null when unknown.
 */
export function typeCurveLifecycleStatus({
  archived,
  isDefault,
  promoted,
}: TypeCurveLifecycleFields): LifecycleStatus | null {
  if (archived) return 'archived';
  if (isDefault) return 'default';
  if (promoted) return 'promoted';
  if (promoted === false) return 'draft';
  return null;
}

/** The lifecycle fields a DCA decline (Well::DcaForecast) carries on the client. */
export interface DcaLifecycleFields {
  /** Promoted at some point (well_dca_forecasts.promoted). */
  promoted?: boolean | null;
  /**
   * Whether this is the newest promoted decline for its well and DCA scenario. False on a
   * promoted decline means a newer one replaced it. Undefined when the API does not say.
   */
  latestPromoted?: boolean | null;
}

/**
 * One status per decline: Archived when a newer promoted decline replaced it, Promoted when it is
 * the one in use, Draft when it was never promoted. Declines have no Default: nothing in
 * well_dca_forecasts marks one, and evaluations take the latest promoted decline. Returns null
 * when the record says nothing about promotion.
 *
 * @param {DcaLifecycleFields} fields - The decline's lifecycle fields.
 * @return {LifecycleStatus | null} The status, or null when unknown.
 */
export function dcaLifecycleStatus({
  promoted,
  latestPromoted,
}: DcaLifecycleFields): LifecycleStatus | null {
  if (promoted) return latestPromoted === false ? 'archived' : 'promoted';
  if (promoted === false) return 'draft';
  return null;
}

/**
 * A workspace (well, geometry, title support...) is Promoted once promoted and a Draft until then.
 *
 * @param {boolean | null | undefined} promoted - Whether the workspace was promoted.
 * @return {'promoted' | 'draft'} The status.
 */
export function workspaceLifecycleStatus(
  promoted: boolean | null | undefined
): 'promoted' | 'draft' {
  return promoted ? 'promoted' : 'draft';
}
